<template>
  <div class="flex min-h-full flex-1 flex-col justify-center pt-24 sm:px-6 lg:px-8">
    <div class="sm:mx-auto sm:w-full sm:max-w-md">
      <WordmarkLogo class="mx-auto" />
    </div>

    <div class="mt-6 sm:mx-auto sm:w-full sm:max-w-[480px]">
      <div class="px-6 pb-6 sm:rounded-lg sm:px-12">
        <p class="mx-auto mb-6 flex justify-center text-xl font-semibold">
          {{ t("msg.setUp2fa") }}
        </p>

        <p v-if="loading" class="text-center text-sm text-muted-foreground">{{ t("msg.loading") }}</p>

        <FormKit
          v-else
          type="form"
          class="space-y-2"
          :actions="false"
          @submit="handleVerify"
          @submit-invalid="globals.focusFirstError"
        >
          <p v-if="errorMessage" class="text-danger">{{ t("msg.error") }}: {{ errorMessage }}</p>

          <p class="mb-6">{{ t("msg.scanQr") }}</p>

          <div class="flex justify-center">
            <canvas ref="qrCanvas"></canvas>
          </div>

          <p class="my-6 text-center text-sm">
            {{ t("msg.orCopyKey") }}
            <CopySpan :value="secretKey" />
          </p>

          <FormKit
            type="text"
            :classes="{ outer: { 'max-w-[20em]': false } }"
            :label="t('msg.code')"
            name="code"
            id="code"
            validation="required"
            autocomplete="one-time-code"
            inputmode="numeric"
            pattern="[0-9]*"
            maxlength="6"
          />

          <div>
            <button
              type="submit"
              :disabled="isSubmitting"
              :class="globals.btnClass(theme, highContrast)"
              class="mt-6 flex w-full items-center justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
            >
              <font-awesome-icon
                v-if="isSubmitting"
                icon="fa-sharp-duotone fa-solid fa-spinner-third"
                spin
                class="mr-2"
              />
              {{ t("msg.verify") }}
            </button>
          </div>

          <button v-if="fromKnoc" type="button" class="cursor-pointer text-center text-white underline" @click="cancel">
            {{ t("common.cancel") }}
          </button>
          <button v-else type="button" class="cursor-pointer text-center text-white underline" @click="logout">
            {{ t("msg.backToLogin") }}
          </button>
        </FormKit>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import QRCode from "qrcode";
import { useColorMode } from "@vueuse/core";

import globals from "../globals";
import { useI18n } from "vue-i18n";
import authService from "../services/auth.service.ts";
import WordmarkLogo from "../components/WordmarkLogo.vue";
import { useAuthStore } from "../pinia/auth.ts";
import { useThemeStore } from "../pinia/theme.ts";
import { performLogout } from "../utils/logoutRedirect";
import { postLoginNavigate } from "../utils/postLoginNavigate";
import CopySpan from "../components/CopySpan.vue";

const props = defineProps({
  fromKnoc: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const authStore = useAuthStore();
const themeStore = useThemeStore();
themeStore.fetchTheme();

const router = useRouter();
const route = useRoute();

const theme = computed(() => themeStore.accent);
const highContrast = computed(() => themeStore.highAccessibility);
const errorMessage = ref("");
const isSubmitting = ref(false);

const qrCanvas = ref(null);

// The form stays hidden until the secret lands. CopySpan renders whatever value it is
// given, so an ungated form shows an empty key beside a blank QR canvas, and offers a
// Verify button for a code the user has no way to generate yet.
const loading = ref(true);

const secretKey = ref("");
const secretUrl = ref("");

useColorMode({
  initialValue: "dark",
});

const handleVerify = async (fields, node) => {
  isSubmitting.value = true;

  try {
    await authService.twoFactorVerify({ Code: fields.code });
    // Force refresh to get updated TOTP status after setup
    const status = await authStore.fetchStatus({ force: true });

    await postLoginNavigate(route, router);
  } catch (error) {
    errorMessage.value = globals.renderError(t, error);
  } finally {
    isSubmitting.value = false;
  }
};

const logout = () => {
  performLogout();
};

const cancel = async () => {
  router.push({ name: "Home" });
};

onMounted(() => {
  authService.twoFactorOnboard().then(
    async (response) => {
      secretKey.value = response.Secret;
      secretUrl.value = response.URL;
      loading.value = false;

      // The canvas sits behind the loading gate, so it does not exist until the gate
      // opens. Wait for that render before drawing into it.
      await nextTick();
      QRCode.toCanvas(qrCanvas.value, secretUrl.value, function (error) {
        if (error) console.error(error);
      });
    },
    (error) => {
      // The error renders inside the form, so the gate has to open either way.
      errorMessage.value = globals.renderError(t, error);
      loading.value = false;
    },
  );
});
</script>
